/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isBoolean from 'lodash/isBoolean';
import { Card, Checkbox, Button, SearchRM as Search, MainHeader, PageLayout } from 'sharedComponents';
import { useHideNavigation } from 'shell/hooks/useHideNavigation';
import RestrictedPage from '../RestrictedPage';

const getLocalToggles = () => JSON.parse(window.localStorage.getItem('toggles')) || {};

export const Toggles = ({ classes, optimizely, config, userId, userADGroup }) => {
  const [features, setFeatures] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [localToggles, setLocalToggles] = React.useState(getLocalToggles());
  const filteredFeatures = features.filter((feat) => feat.key.indexOf(search.toLowerCase()) > -1);
  const localEdit = config.get('optimizely')?.localEdit;

  if (!localEdit) {
    return <RestrictedPage />;
  }

  useHideNavigation();

  React.useEffect(() => {
    optimizely.dataReadyPromise.then(() => {
      const { featureFlags } = optimizely.client.projectConfigManager.getConfig();
      const flags = featureFlags.map((feat) => {
        const localValue = localToggles?.[feat.key];
        const isFeatureEnabled = optimizely.isFeatureEnabled(feat.key, userId, { ad_groups: userADGroup });
        const toggleVariables = optimizely.getFeatureVariables(feat.key);
        const featureValue = isEmpty(toggleVariables)
          ? isFeatureEnabled
          : { value: isFeatureEnabled, ...toggleVariables };
        return {
          ...feat,
          isEnabled: isFeatureEnabled,
          localValue: localValue === undefined ? featureValue : localValue,
          isLocal: localValue !== undefined,
        };
      });
      setFeatures(flags);
    });
  }, [localToggles]);

  const resetToDefault = () => {
    setLocalToggles({});

    window.localStorage.removeItem('toggles');
  };

  const handleChange = (value) => {
    setSearch(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const recordLocalToggles = (localToggles) => {
    window.localStorage.setItem('toggles', JSON.stringify(localToggles));
    setLocalToggles(localToggles);
  };

  const changeLocalToggle = (event, key) => {
    const value = event.target.checked;
    const toggleVariables = optimizely.getFeatureVariables(key);
    const _localToggles = {
      ...localToggles,
      [key]: isEmpty(toggleVariables) ? value : { value, ...toggleVariables },
    };
    recordLocalToggles(_localToggles);
  };

  const onToggleReset = (key) => {
    const _localToggles = getLocalToggles();
    delete _localToggles[key];

    recordLocalToggles(_localToggles);
  };

  const toggleRow = (feature) => {
    return (
      <Card classes={{ card: classes.card }} bordered={false} key={feature.key}>
        <div className={classes.title}>{feature.key}</div>
        <div>
          <Checkbox className={classes.checkbox} checked={!!feature.isEnabled} disabled>
            <span className={classes.label}>Is Enabled</span>
          </Checkbox>
          <Checkbox
            className={classes.checkbox}
            checked={isBoolean(feature.localValue) ? !!feature.localValue : !!feature?.localValue?.value}
            onChange={(event) => changeLocalToggle(event, feature.key)}
            data-testid="toggles-change"
          >
            <span className={classes.label}>Enabled Locally</span>
          </Checkbox>
          {feature.isLocal && (
            <span className={classes.resetBtn} role="button" tabIndex={0} onClick={() => onToggleReset(feature.key)}>
              Reset to Default
            </span>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Fragment>
      <MainHeader title="Feature toggles" />
      <PageLayout>
        <div>
          <div className={classes.toggleHeader}>
            <Button
              className={classes.btn}
              label="Reset to default"
              onClick={resetToDefault}
              data-testid="toggles-reset"
              disabled={isEmpty(localToggles)}
            />
            <Search
              placeholder="Search..."
              className={classes.toggleHeader}
              search={search}
              applySearch={handleChange}
            />
          </div>
          <div>
            {!filteredFeatures.length && <div>No Matches Found</div>}
            {filteredFeatures.map((feature) => toggleRow(feature, false))}
          </div>
        </div>
      </PageLayout>
    </Fragment>
  );
};

Toggles.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
};

Toggles.defaultProps = {
  classes: {},
};

export default Toggles;
